import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@follosoft/react-components";
import { ContractResource, ProspectionServiceResource } from "@/interfaces"; // adjust if needed

export function useContract(contractId?: number | string | null) {
  return useQuery<ContractResource>({
    queryKey: ["contract", Number(contractId)],
    queryFn: async () => {
      if (!contractId) {
        throw new Error("Contract ID is required");
      }

      const includeParams = [
        "with_prices=true",
        "with_signature=true",
        // latestSubscription as well as subscriptionActive: a plan paid in one
        // payment completes immediately, so subscriptionActive is null exactly when
        // the contract is settled — without this the agreed plan is lost the moment
        // the customer finishes paying for it.
        "include=address,company,subscriptionActive.schedule.invoice.status,latestSubscription.schedule.invoice.status,belongsToManyServices,rebates,rebates.type",
      ].join("&");

      const res = await axiosInstance().get(
        `/folloportal/contracts/${contractId}?${includeParams}`
      );

      return res.data.data;
    },
    enabled: !!contractId,
    staleTime: 0,
  });
}

export function useProspectionServices(contractId?: number | string) {
  return useQuery<ProspectionServiceResource[]>({
    queryKey: ["prospection-services", Number(contractId)],
    queryFn: async () => {
      if (!contractId) return [];

      const res = await axiosInstance().get(
        `/folloportal/contracts/${contractId}/prospection-services`
      );

      return res.data.data;
    },
    enabled: !!contractId,
    staleTime: 0,
  });
}

export function useAttachProspectionServicesMutation(){
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, serviceId, priceModeId }: { contractId: number | string, serviceId: number | string, priceModeId?: number | string | null }) => {
      return axiosInstance().post(
        `/folloportal/contracts/${contractId}/prospection-services`,
        {
          serviceId,
          priceModeId: priceModeId ?? null,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["prospection-services"],
      });
      // The attached service gives the contract its real totals, which is what
      // payment-plan and promotion eligibility are computed from — the pre-attach
      // responses were built on $0 totals and would hide an applicable promotion.
      queryClient.invalidateQueries({
        queryKey: ["available-payment-plans"],
      });
      queryClient.invalidateQueries({
        queryKey: ["online-payment-options"],
      });
      // The contract itself carries the totals the summary renders. Without this the
      // customer attaches a service and still sees $0.00 on the payment steps, which
      // reads exactly like the selection was lost.
      queryClient.invalidateQueries({
        queryKey: ["contract"],
      });
    },
  });
}